import React from 'react'
import { SxProps, Theme, styled } from '@mui/material/styles'
import { brandedTokens } from '@/theme/tokens'

export interface BadgeTagProps {
    children: React.ReactNode
    variant?:
        | 'default'
        | 'primary'
        | 'secondary'
        | 'success'
        | 'warning'
        | 'error'
        | 'selected'
    color?: string
    textColor?: string
    size?: 'small' | 'medium' | 'large'
    clickable?: boolean
    onClick?: () => void
    sx?: SxProps<Theme>
}

export const BadgeTag = styled('span', {
    shouldForwardProp: (prop) =>
        !['variant', 'size', 'clickable', 'color', 'textColor', 'sx'].includes(
            prop as string
        ),
})<BadgeTagProps>(
    ({
        theme,
        variant = 'default',
        size = 'small',
        clickable,
        color,
        textColor,
    }) => {
        const variants = {
            default: {
                backgroundColor: brandedTokens.variables.secondary,
                color: theme.palette.common.white,
            },
            primary: {
                backgroundColor: brandedTokens.variables.primary,
                color: brandedTokens.brand.text.primary,
            },
            secondary: {
                backgroundColor: brandedTokens.variables.secondary,
                color: theme.palette.common.white,
            },
            success: {
                backgroundColor: theme.palette.success.main,
                color: theme.palette.success.contrastText,
            },
            warning: {
                backgroundColor: theme.palette.warning.main,
                color: theme.palette.warning.contrastText,
            },
            error: {
                backgroundColor: theme.palette.error.main,
                color: theme.palette.error.contrastText,
            },
            selected: {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
            },
        }

        const sizes = {
            small: {
                padding: `${theme.spacing(0.25)} ${theme.spacing(0.75)}`,
                fontSize: theme.typography.caption.fontSize,
            },
            medium: {
                padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
                fontSize: theme.typography.body2.fontSize,
            },
            large: {
                padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
                fontSize: theme.typography.body1.fontSize,
            },
        }

        const variantStyles = variants[variant]
        const finalBackground = color || variantStyles.backgroundColor
        const finalText = textColor || (color ? '#fff' : variantStyles.color)
        return {
            display: 'inline-flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            borderRadius: theme.shape.borderRadius,
            fontWeight: 500,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            cursor: clickable ? 'pointer' : 'default',
            transition: 'all 0.15s ease',

            ...variants[variant],
            ...sizes[size],

            backgroundColor: finalBackground,
            color: finalText,

            ...(clickable && {
                '&:hover': {
                    filter: 'brightness(0.95)',
                },
            }),
        }
    }
)
